import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ScrollText, ShieldCheck } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Count, EmptyState, Page, PageHeader, Pager, TableCard, Toolbar } from '@/components/shell'
import { api, type AuditRow, type Paginated } from '@/lib/api'
import { isRecord, recordAt } from '@/lib/json'
import {
  auditActionLabel,
  AUDIT_ACTION_LABELS,
  formatDateTime,
  formatPauseUntil,
  hideCategoryLabel,
  pauseKindLabel,
  shortAddress,
} from '@/lib/format'

export const Route = createFileRoute('/dashboard/audit')({
  component: AuditPage,
})

const WARN_TONE = 'bg-warn/15 text-warn hover:bg-warn/15'
const ROSE_TONE = 'bg-rose/15 text-rose hover:bg-rose/15'

/**
 * 动作按影响面着色：下架/停用是限制性操作，用 warn；恢复类用中性。
 *
 * 合约类动作里，**能把用户挡在外面或者不可逆的那几个用 rose**——紧急开关和
 * 所有权变更属于「做完之后不是改回来就行」的一类，和「加一个免税端点」不该同色。
 */
const ACTION_TONE: Record<string, string> = {
  'token.hide': WARN_TONE,
  'wallet.bulkHide': WARN_TONE,
  'user.disable': WARN_TONE,
  'user.session.revoke': WARN_TONE,
  'token.hot.on': ROSE_TONE,
  'token.hot.off': 'bg-edge/10 text-mut hover:bg-edge/10',
  'contract.detectedPool.add': 'bg-cyan/15 text-cyan hover:bg-cyan/15',
  'contract.taxExempt.add': 'bg-green/15 text-green hover:bg-green/15',
  'contract.taxExempt.remove': WARN_TONE,
  'contract.poolFee.set': WARN_TONE,
  'contract.pause.set': ROSE_TONE,
  'contract.signer.set': WARN_TONE,
  'contract.owner.transfer': ROSE_TONE,
  'contract.owner.accept': ROSE_TONE,
}

/** 审计日志页。仅 admin 可见——菜单里对其他角色根本不出现，接口也会 403。 */
function AuditPage() {
  const [page, setPage] = useState(1)
  const [action, setAction] = useState('all')
  const [targetType, setTargetType] = useState('all')

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', { page, action, targetType }],
    queryFn: () =>
      api.get<Paginated<AuditRow>>('/audit-logs', {
        page,
        perPage: 25,
        action: action === 'all' ? undefined : action,
        targetType: targetType === 'all' ? undefined : targetType,
      }),
  })

  return (
    <Page>
      <PageHeader
        title="审计日志"
        description="全部运营操作的流水。只增不改——数据库层已收回运行角色的 UPDATE 与 DELETE 权限。"
      />

      <div className="surface-inset flex items-start gap-2 px-4 py-2.5">
        <ShieldCheck className="mt-px size-3.5 shrink-0 text-green" />
        <p className="text-[11.5px] leading-relaxed text-mut2">
          这些记录在数据库层不可篡改：应用连接对该表只有 INSERT / SELECT 权限，
          即使后台代码出错也改不动历史。要清理需要 DBA 凭据。
        </p>
      </div>

      <Toolbar trailing={<Count value={data?.total} />}>
        <Select
          value={action}
          onValueChange={(v) => {
            setAction(v)
            setPage(1)
          }}
        >
          <SelectTrigger className="h-9 w-36 rounded-apple text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部动作</SelectItem>
            {Object.entries(AUDIT_ACTION_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={targetType}
          onValueChange={(v) => {
            setTargetType(v)
            setPage(1)
          }}
        >
          <SelectTrigger className="h-9 w-28 rounded-apple text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部对象</SelectItem>
            <SelectItem value="token">代币</SelectItem>
            <SelectItem value="user">账号</SelectItem>
            <SelectItem value="role">角色</SelectItem>
            <SelectItem value="wallet">钱包</SelectItem>
            <SelectItem value="contract">合约</SelectItem>
          </SelectContent>
        </Select>
      </Toolbar>

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow className="border-line/50 hover:bg-transparent">
              <TableHead className="section-label pl-6">时间</TableHead>
              <TableHead className="section-label">操作人</TableHead>
              <TableHead className="section-label">动作</TableHead>
              <TableHead className="section-label">对象</TableHead>
              <TableHead className="section-label pr-6">详情</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 8 }, (_, i) => (
                <TableRow key={i} className="border-line/40">
                  <TableCell colSpan={5} className="px-6 py-2.5">
                    <Skeleton className="h-7 w-full rounded-apple-sm" />
                  </TableCell>
                </TableRow>
              ))}

            {data?.data.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={5}>
                  <EmptyState
                    icon={ScrollText}
                    title="暂无操作记录"
                    description="运营执行下架、打热门、改角色等操作后，会在这里留下不可篡改的流水。"
                  />
                </TableCell>
              </TableRow>
            )}

            {data?.data.map((row) => (
              <TableRow key={row.id} className="row-hover border-line/40">
                <TableCell className="tnum whitespace-nowrap pl-6 font-mono text-[11px] text-mut2">
                  {formatDateTime(row.createdAt)}
                </TableCell>

                <TableCell>
                  <span className="font-mono text-[11.5px] text-txt">{row.actorUsername}</span>
                  {!row.actor && (
                    <span className="ml-1.5 text-[10.5px] text-dim">（账号已删除）</span>
                  )}
                </TableCell>

                <TableCell>
                  <Badge
                    className={`h-5 text-[10.5px] ${ACTION_TONE[row.action] ?? 'bg-edge/10 text-mut hover:bg-edge/10'}`}
                  >
                    {auditActionLabel(row.action)}
                  </Badge>
                </TableCell>

                <TableCell>
                  {row.targetType === 'token' ? (
                    <Link
                      to="/dashboard/tokens/$address"
                      params={{ address: row.targetId }}
                      className="font-mono text-[11.5px] text-mut hover:text-cyan"
                    >
                      {shortAddress(row.targetId)}
                    </Link>
                  ) : row.targetType === 'wallet' ? (
                    <Link
                      to="/dashboard/wallets"
                      search={{ address: row.targetId }}
                      className="font-mono text-[11.5px] text-mut hover:text-cyan"
                    >
                      {shortAddress(row.targetId)}
                    </Link>
                  ) : (
                    <span className="font-mono text-[11.5px] text-mut2">
                      {row.targetId.startsWith('0x') ? shortAddress(row.targetId) : row.targetId}
                    </span>
                  )}
                </TableCell>

                <TableCell className="max-w-lg pr-6">
                  <PayloadSummary payload={row.payload} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>

      <Pager page={page} pageCount={data?.pageCount ?? 1} onChange={setPage} />
    </Page>
  )
}

/** 把异构的 payload 压成一句人话。认不出的结构不硬猜，显示破折号。 */
function PayloadSummary({ payload }: { payload: unknown }) {
  if (!isRecord(payload)) return <span className="text-dim">—</span>

  const parts: string[] = []

  if (typeof payload.category === 'string') parts.push(hideCategoryLabel(payload.category))
  if (typeof payload.reason === 'string' && payload.reason) parts.push(payload.reason)

  /**
   * ⚠️ `before` / `after` 也必须过 `isRecord`，**不能只判 truthy 再断言**。
   * 原先这里是 `payload.before as Record<...>` 然后 `'role' in b`——
   * `in` 作用在原始值上抛 TypeError，一条 `{ before: "viewer" }` 就是白屏。
   * 顶层守住了、下一层照旧断言，等于守了个寂寞。
   */
  const before = recordAt(payload, 'before')
  const after = recordAt(payload, 'after')

  if (before && after) {
    if ('role' in before) parts.push(`${String(before.role)} → ${String(after.role)}`)
    if (Array.isArray(before.menuIds) && Array.isArray(after.menuIds)) {
      parts.push(`${before.menuIds.length} → ${after.menuIds.length} 个菜单`)
    }
    if ('hotWeight' in after && after.hot) parts.push(`权重 ${String(after.hotWeight)}`)
  }

  if (typeof payload.count === 'number') parts.push(`共 ${payload.count} 个代币`)
  if (typeof payload.failedCount === 'number' && payload.failedCount > 0) {
    parts.push(`${payload.failedCount} 个失败`)
  }
  if (typeof payload.revokedSessions === 'number') {
    parts.push(`吊销 ${payload.revokedSessions} 个会话`)
  }

  /**
   * 紧急开关：`until` 是 uint64 的 Unix 秒，写成字符串存的。
   * `'0'` 代表恢复——这是这条记录里最要紧的一个字，不能因为它是个 0 就被吞掉。
   */
  if (typeof payload.pauseKind === 'number') {
    parts.push(pauseKindLabel(payload.pauseKind))
    if (typeof payload.until === 'string') {
      parts.push(payload.until === '0' ? '恢复' : `暂停至 ${formatPauseUntil(payload.until)}`)
    }
  }

  // 批量合约操作只记 tokenCount，不逐个列地址——完整清单在 payload 原文里。
  if (typeof payload.tokenCount === 'number') parts.push(`批量 ${payload.tokenCount} 个代币`)
  if (typeof payload.targetAddress === 'string') {
    parts.push(`目标 ${shortAddress(payload.targetAddress)}`)
  }
  if (typeof payload.transactionFrom === 'string') {
    parts.push(`发起 ${shortAddress(payload.transactionFrom)}`)
  }
  if (typeof payload.txHash === 'string') {
    parts.push(`交易 ${shortAddress(payload.txHash)}`)
  }
  if (typeof payload.blockNumber === 'string') parts.push(`区块 ${payload.blockNumber}`)

  if (parts.length === 0) return <span className="text-[11.5px] text-dim">—</span>

  return (
    <span className="line-clamp-2 text-[11.5px] leading-relaxed text-mut">{parts.join(' · ')}</span>
  )
}
